"use client";

import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  BookOpen,
  Clock,
  LayoutDashboard,
  RefreshCw,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { MissionData } from "@/types/mission";

/* ---------------------------------------------------------------------------
 * Wake cadence card — shows the autonomous scheduling layer (state.schedule):
 * each role, its cadence, purpose, and a live next-fire countdown.
 * Read-only view over the operator-owned state; this component never writes.
 * ------------------------------------------------------------------------- */

const ROLE_META: Record<string, { icon: LucideIcon; cls: string }> = {
  "mission-loop": { icon: RefreshCw, cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400" },
  "deep-research": { icon: BookOpen, cls: "border-violet-500/30 bg-violet-500/10 text-violet-300" },
  "monitor-verify": { icon: Activity, cls: "border-teal-500/30 bg-teal-500/10 text-teal-300" },
  "dashboard-qa": { icon: LayoutDashboard, cls: "border-sky-500/30 bg-sky-500/10 text-sky-300" },
};

function roleIcon(name: string): { Icon: LucideIcon; cls: string } {
  const hit = ROLE_META[name] ?? (name.startsWith("one-shot") ? { icon: Zap, cls: "border-amber-500/30 bg-amber-500/10 text-amber-300" } : { icon: Clock, cls: "border-zinc-700 bg-zinc-800/40 text-zinc-400" });
  return { Icon: hit.icon, cls: hit.cls };
}

/** current offset (minutes) of a tz vs UTC, via Intl — DST-safe enough for UTC/IST */
function tzOffsetMinutes(date: Date, tz: string): number {
  try {
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    const parts = dtf.formatToParts(date).reduce<Record<string, string>>((acc, p) => {
      acc[p.type] = p.value;
      return acc;
    }, {});
    const asUTC = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour) % 24,
      Number(parts.minute),
      Number(parts.second),
    );
    return Math.round((asUTC - date.getTime()) / 60000);
  } catch {
    return 0;
  }
}

/** parse a cron field ("0", "44", "1,7,13,19", or step syntax like star-slash-15) into a match set */
function fieldSet(field: string, mod: number): Set<number> | null {
  if (field === "*" || field === "?") return null; // wildcard → no constraint
  const out = new Set<number>();
  for (const part of field.split(",")) {
    const step = part.split("/");
    const base = step[0];
    const inc = step.length > 1 ? Number(step[1]) : 1;
    if (!Number.isFinite(inc) || inc < 1) return new Set<number>([0]);
    if (base === "*") {
      for (let v = 0; v < mod; v += inc) out.add(v);
    } else {
      const v = Number(base);
      if (Number.isFinite(v)) out.add(v);
    }
  }
  return out;
}

interface NextFire {
  /** epoch ms of the next fire, in the item's tz wall-clock resolved to UTC */
  at: number;
  cadence: string;
}

/** next-fire estimation for the simple cron shapes this mission uses:
 *  "0 44 1,7,13,19 ...", "0 0 9 ...", "0 star-slash-15 ..." (sec/min/hour constrained) */
function nextFire(expr: string, tz: string, now: Date): NextFire | null {
  const f = expr.trim().split(/\s+/);
  if (f.length < 6) return null;
  const secs = fieldSet(f[0], 60);
  const mins = fieldSet(f[1], 60);
  const hours = fieldSet(f[2], 24);
  if (!mins && !hours && !secs) return null; // fully wildcard — no anchor, skip countdown

  const offset = tzOffsetMinutes(now, tz);
  // wall-clock "now" expressed as a UTC timestamp
  const wallNow = new Date(now.getTime() + offset * 60000);
  let t = new Date(Math.floor(wallNow.getTime() / 60000) * 60000 + 60000); // next whole minute

  const hourList = hours ? [...hours].sort((a, b) => a - b) : null;
  const cadence = hourList
    ? hourList.length > 1
      ? `${hourList.length}×/day @ :${[...(mins ?? new Set([0]))].sort((a, b) => a - b).join(",")} ${tz === "UTC" ? "UTC" : tz}`
      : `daily ${String(hourList[0]).padStart(2, "0")}:${String([...(mins ?? new Set([0]))][0] ?? 0).padStart(2, "0")} ${tz === "UTC" ? "UTC" : tz}`
    : mins && [...mins].length > 1
      ? `every ${60 / [...mins].length}m`
      : "hourly";

  for (let i = 0; i < 60 * 24 * 3; i++) {
    if ((!secs || secs.has(t.getUTCSeconds())) && (!mins || mins.has(t.getUTCMinutes())) && (!hours || hours.has(t.getUTCHours()))) {
      return { at: t.getTime() - offset * 60000, cadence };
    }
    t = new Date(t.getTime() + 60000);
  }
  return null;
}

function fmtCountdown(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, "0")}s`;
  return `${sec}s`;
}

function timeAgoShort(ts: string, now: number): string {
  const t = Date.parse(ts);
  if (!Number.isFinite(t)) return "?";
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export function WakeCadenceCard({ data }: { data: MissionData }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const tick = window.setInterval(() => {
      if (!document.hidden) setNow(Date.now());
    }, 1000);
    return () => window.clearInterval(tick);
  }, []);

  /* last fire per role family — derived honestly from actions.json types.
     Historical type strings are heterogeneous ("monitor", "monitoring",
     "monitor-verify", "deep-research", "research", …), so roles map to
     type-family prefixes; entries without a family match (webDevReview /
     one-shot roles log no ACT by design) are shown as such, never guessed. */
  const lastFire = useMemo(() => {
    const families: Record<string, (t: string) => boolean> = {
      "mission-loop": (t) => t === "mission-loop",
      "monitor-verify": (t) => t.startsWith("monitor"),
      "deep-research": (t) => t.startsWith("deep-research") || t === "research",
    };
    const out: Record<string, { id: string; ts: string; ago: string } | null> = {};
    for (const [role, match] of Object.entries(families)) {
      let best: { id: string; ts: string } | null = null;
      for (const a of data.actions) {
        const t = a.type ?? "";
        if (match(t) && (!best || (a.ts ?? "") > best.ts)) {
          best = { id: a.id, ts: a.ts ?? "" };
        }
      }
      out[role] = best ? { ...best, ago: timeAgoShort(best.ts, now) } : null;
    }
    return out;
  }, [data.actions, now]);

  const roles = useMemo(() => {
    const nowD = new Date(now);
    return (data.state.schedule ?? []).map((s) => {
      if (s.every_seconds) {
        return { ...s, next: null as number | null, cadence: `every ${s.every_seconds}s` };
      }
      const nf = s.expr ? nextFire(s.expr, s.tz ?? "UTC", nowD) : null;
      return { ...s, next: nf ? nf.at : null, cadence: nf ? nf.cadence : s.expr ?? s.kind };
    });
  }, [data.state.schedule, now]);

  if (roles.length === 0) return null;
  const soonest = roles
    .filter((r) => typeof r.next === "number")
    .sort((a, b) => (a.next ?? Infinity) - (b.next ?? Infinity))[0];

  return (
    <Card className="min-w-0 border-zinc-800/70 bg-zinc-900/40 shadow-none">
      <CardHeader className="pb-3">
        <CardTitle className="flex min-w-0 flex-wrap items-center justify-between gap-2 font-mono text-xs font-semibold uppercase tracking-[0.14em] text-zinc-400">
          <span className="flex min-w-0 items-center gap-2">
            <Clock className="size-3.5 shrink-0 text-emerald-400" aria-hidden="true" />
            Wake Cadence
            <span className="font-normal normal-case tracking-normal text-zinc-600">
              the autonomous scheduling layer
            </span>
          </span>
          {soonest && (
            <Badge
              variant="outline"
              className="border-emerald-500/40 bg-emerald-500/10 font-mono text-[10px] text-emerald-400"
            >
              next role up in {fmtCountdown((soonest.next ?? 0) - now)}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="min-w-0">
        <ul className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {roles.map((r) => {
            const { Icon, cls } = roleIcon(r.name);
            const isNext = soonest && r.name === soonest.name;
            const until = typeof r.next === "number" ? r.next - now : null;
            const last = lastFire[r.name];
            return (
              <li
                key={`${r.name}-${r.job_id}`}
                className={`group flex min-w-0 flex-col gap-2 rounded-lg border p-3 transition-all duration-150 focus-within:ring-1 focus-within:ring-emerald-500/60 ${
                  isNext
                    ? "border-emerald-500/30 bg-emerald-500/[0.06]"
                    : "border-zinc-800/70 bg-zinc-950/40 hover:border-zinc-700/80 hover:bg-zinc-900/50"
                }`}
              >
                <div className="flex min-w-0 items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-2">
                    <span
                      className={`flex size-7 shrink-0 items-center justify-center rounded-md border ${cls}`}
                      aria-hidden="true"
                    >
                      <Icon className="size-3.5" />
                    </span>
                    <span className="truncate font-mono text-xs font-bold text-zinc-100">{r.name}</span>
                  </span>
                  {until !== null ? (
                    <span
                      className={`shrink-0 font-mono text-[10px] font-semibold tabular-nums ${
                        until < 5 * 60_000 ? "text-emerald-400" : "text-zinc-500"
                      }`}
                      title="estimated next fire"
                    >
                      {until <= 0 ? "firing…" : `in ${fmtCountdown(until)}`}
                    </span>
                  ) : (
                    <span className="shrink-0 font-mono text-[10px] text-zinc-600">{r.kind}</span>
                  )}
                </div>
                <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                  <Badge variant="outline" className="border-zinc-700 bg-zinc-800/40 font-mono text-[10px] text-zinc-400">
                    {r.cadence}
                  </Badge>
                  <Badge variant="outline" className="border-zinc-800 bg-zinc-900/40 font-mono text-[10px] text-zinc-600">
                    job {r.job_id}
                  </Badge>
                  {last ? (
                    <Badge
                      variant="outline"
                      className="border-zinc-800 bg-zinc-900/40 font-mono text-[10px] text-zinc-500"
                      title={`${last.id} @ ${last.ts} — latest actions.json entry in this role's type family`}
                    >
                      last {last.id} · {last.ago}
                    </Badge>
                  ) : (
                    r.name !== "dashboard-qa" &&
                    !r.name.startsWith("one-shot") && (
                      <Badge
                        variant="outline"
                        className="border-zinc-800 bg-zinc-900/40 font-mono text-[10px] text-zinc-600"
                        title="no actions.json entry attributed to this role family yet"
                      >
                        no ACT logged
                      </Badge>
                    )
                  )}
                </div>
                {r.purpose && (
                  <p className="line-clamp-2 text-[11px] leading-snug text-zinc-500" title={r.purpose}>
                    {r.purpose}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
        <p className="mt-3 text-[11px] leading-snug text-zinc-600">
          Estimates derived from the recorded schedule spec (state.json) — countdowns are wall-clock
          projections, not platform guarantees. Role set is managed by the orchestrator (D-013).
        </p>
      </CardContent>
    </Card>
  );
}
